import { useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';

import { Avatar, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { editProfileSchema } from 'validation/form/profile';
import { listStates } from 'store/slices/properties';

import { useProfileDrawers } from 'hooks/useProfileDrawers';
import { useProfile } from 'hooks/useProfile';

import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { EntityFileDetails } from 'components/shared/EntityFileDetails';
import { FormTextField } from 'components/common/form/Form';
import { AddressField } from 'components/common/form/AddressField';

export const EditProfile = (props) => {
  const { close } = props;
  const dispatch = useDispatch();

  const { openDrawerProfileDetails } = useProfileDrawers();
  const { handleUpdateProfile } = useProfile();

  const currentUser = useSelector((state) => state.auth.currentUser);
  const loading = useSelector((state) => state.auth.editProfileLoading);

  const defaultValues = useMemo(() => {
    return {
      contact: {
        address: {
          address: '',
          suburb: '',
          state: '',
          postcode: '',
          place_id: '',
        },
        phone: '',
      },
      name: '',
      email: '',
    };
  }, []);

  const methods = useForm({
    resolver: yupResolver(editProfileSchema),
    defaultValues,
  });

  const handleSave = methods.handleSubmit((data) => {
    const { contact, name, email } = data;
    const payload = { contact, name, email };
    handleUpdateProfile(payload, close);
  });

  const handleCancel = () => {
    openDrawerProfileDetails();
  };

  const handleFetchProfileEnums = useCallback(async () => {
    return await Promise.all([dispatch(listStates())]);
  }, [dispatch]);

  useEffect(() => {
    handleFetchProfileEnums();
  }, [handleFetchProfileEnums]);

  useEffect(() => {
    if (currentUser) {
      methods.reset({ ...currentUser });
    }
  }, [methods, currentUser]);

  return (
    <DrawerContent>
      <DrawerContent.Header title="Edit Profile" />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <Stack
            alignItems="center"
            spacing={0.5}
            pb={1}
          >
            <EntityFileDetails.FileType
              fileIcon={
                <Avatar sx={{ width: 90, height: 90, fontSize: 35 }}>
                  {currentUser?.name
                    ?.split(' ')
                    .slice(0, 2)
                    .map((word) => word.charAt(0).toUpperCase())
                    .join('')}
                </Avatar>
              }
            />
            {/* TODO: change profile photo */}
            {/* <Link
              onClick={() => null}
              variant="body2"
              underline="none"
              color="primary"
              sx={{ cursor: 'pointer' }}
            >
              Change Profile Photo
            </Link> */}
          </Stack>
          <FormProvider {...methods}>
            <FormTextField
              name="name"
              label="Name"
              control={methods.control}
            />
            <FormTextField
              name="email"
              label="Email"
              control={methods.control}
              disabled
            />
            <AddressField
              currentAddress={{ value: methods.watch('contact.address') }}
              callbacks={{
                toggleSetValue: (updatedAddress) => {
                  methods.setValue(
                    'contact.address',
                    { ...updatedAddress, place_id: updatedAddress?.place_id ?? '' },
                    { shouldValidate: true }
                  );
                },
              }}
            />
          </FormProvider>
        </DrawerContent.Content>
        <DrawerContent.ActionButtons
          submitButtonText="Save"
          submitButtonLoading={loading}
          submit={handleSave}
          close={handleCancel}
        />
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
